import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { formatCurrency as formatLocaleCurrency, formatDate as formatLocaleDate, formatDateTime as formatLocaleDateTime } from "@/lib/formatters";
import { applyLanguageDocument, getStoredLanguage, normalizeLanguage } from "@/i18n/catalogs";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Read tenant currency from localStorage (set on login). The INR fallback is
// retained only for unauthenticated/legacy India tenants.
const _currency = () => {
  try {
    const raw = localStorage.getItem("eduos_tenant_currency");
    if (raw) return JSON.parse(raw);
  } catch {}
  return { symbol: "₹", code: "INR", locale: "en-IN" };
};

// Apply RTL direction on <html> based on the user's language preference.
export const applyLangDir = () => {
  return applyLanguageDocument(getStoredLanguage());
};

export const setLang = (lang) => {
  const normalized = normalizeLanguage(lang);
  try { localStorage.setItem("eduos_lang", normalized); } catch {}
  return applyLanguageDocument(normalized);
};

// Money handling: integer minor units (paise for INR, fils for AED) -> major
// unit display. Keep the old helper name as a compatibility alias.
export const minorToMajor = (minorUnits) => (minorUnits || 0) / 100;
export const paiseToRupees = minorToMajor;

// Historical name retained for compatibility; values are tenant minor units.
export const formatMoney = (minorUnits) => {
  const cur = _currency();
  return formatLocaleCurrency(minorUnits || 0, cur.code || "INR", getStoredLanguage(), {
    minimumFractionDigits: (minorUnits || 0) % 100 === 0 ? 0 : 2,
  });
};
export const formatINR = formatMoney;

export const formatCurrency = (minorUnits, currency, language) =>
  formatLocaleCurrency(minorUnits, currency || _currency().code || "INR", language || getStoredLanguage());

export const formatDate = (iso, language) => formatLocaleDate(iso, language || getStoredLanguage(), {
  currency: _currency().code || "INR",
});

export const formatDateTime = (iso, language) => formatLocaleDateTime(iso, language || getStoredLanguage(), {
  currency: _currency().code || "INR",
});

export const today = () => new Date().toISOString().slice(0, 10);

export const statusTone = (status) => {
  const s = (status || "").toLowerCase();
  if (["paid", "success", "enrolled", "active", "present", "published"].includes(s)) return "success";
  if (["overdue", "rejected", "failed", "absent"].includes(s)) return "error";
  if (["partially_paid", "late", "shortlisted", "interview_scheduled", "offered"].includes(s)) return "warning";
  return "info";
};
